import React, { useState, useEffect } from "react";
import {
  Page,
  Card,
  DataTable,
  Text,
  BlockStack,
  Tabs,
  ProgressBar,
  Box,
  Divider,
  Layout,
  Modal,
  Button,
  InlineStack,
  Badge,
  Spinner,
} from "@shopify/polaris";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import FilterBar from "../../Components/Analytics/FilterBar";
import analyticsApi from "../../Services/analyticsApi";

const COLORS = ["#5C6AC4", "#47C1BF", "#F49342", "#DC3545", "#00A6A0", "#9C6ADE"];

const Products = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    include_manual: false,
    limit: 20,
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const [bestSellersData, setBestSellersData] = useState({
    courses: null,
    lectures: null,
    notes: null,
  });
  const [subjectData, setSubjectData] = useState(null);
  const [universityData, setUniversityData] = useState(null);
  const [engagementData, setEngagementData] = useState(null);
  const [modalActive, setModalActive] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalType, setModalType] = useState(null); // 'course' or 'lecture'

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [
        coursesResponse,
        lecturesResponse,
        notesResponse,
        subjectsResponse,
        universitiesResponse,
        engagementResponse,
      ] = await Promise.all([
        analyticsApi.getBestSellingCourses(filters),
        analyticsApi.getBestSellingLectures(filters),
        analyticsApi.getBestSellingNotes(filters),
        analyticsApi.getProductsBySubject(filters),
        analyticsApi.getProductsByUniversity(filters),
        analyticsApi.getEngagementMetrics(filters),
      ]);

      setBestSellersData({
        courses: coursesResponse.data.data,
        lectures: lecturesResponse.data.data,
        notes: notesResponse.data.data,
      });
      setSubjectData(subjectsResponse.data.data);
      setUniversityData(universitiesResponse.data.data);
      setEngagementData(engagementResponse.data.data);
    } catch (error) {
      console.error("Error fetching products data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleViewEnrolledUsers = async (type, id, name) => {
    setModalType(type);
    setModalActive(true);
    setModalLoading(true);
    setModalData(null);

    try {
      let response;
      if (type === 'course') {
        response = await analyticsApi.getCourseEnrolledUsers(id);
      } else if (type === 'lecture') {
        response = await analyticsApi.getLectureEnrolledUsers(id);
      }

      setModalData({
        ...response.data.data,
        productName: name,
      });
    } catch (error) {
      console.error("Error fetching enrolled users:", error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalActive(false);
    setModalData(null);
    setModalType(null);
  };

  if (isLoading) {
    return (
      <Page title="Product Analytics" backAction={{ content: "Back", url: "/admin/analytics" }}>
        <BlockStack gap="400">
          <Card>
            <Box padding="400">
              <Text>Loading product data...</Text>
            </Box>
          </Card>
        </BlockStack>
      </Page>
    );
  }

  // Prepare best sellers chart data based on selected tab
  const getBestSellersChartData = () => {
    if (selectedTab === 0 && bestSellersData.courses?.courses) {
      return bestSellersData.courses.courses.map((course) => ({
        name: course.name_en || course.name_ar || "N/A",
        sales: course.sales_count,
        revenue: course.total_revenue,
      }));
    } else if (selectedTab === 1 && bestSellersData.lectures?.lectures) {
      return bestSellersData.lectures.lectures.map((lecture) => ({
        name: lecture.name_en || lecture.name_ar || "N/A",
        sales: lecture.sales_count,
        revenue: lecture.total_revenue,
      }));
    } else if (selectedTab === 2 && bestSellersData.notes?.notes) {
      return bestSellersData.notes.notes.map((note) => ({
        name: note.name_en || note.name_ar || "N/A",
        sales: note.sales_count,
        revenue: note.total_revenue,
      }));
    }
    return [];
  };

  // Prepare best sellers table data based on selected tab
  const getBestSellersTableRows = () => {
    if (selectedTab === 0 && bestSellersData.courses?.courses) {
      return bestSellersData.courses.courses.map((course) => [
        course.name_en || course.name_ar || "N/A",
        course.sales_count,
        `${course.total_revenue.toFixed(2)} KWD`,
        `${course.average_price.toFixed(2)} KWD`,
      ]);
    } else if (selectedTab === 1 && bestSellersData.lectures?.lectures) {
      return bestSellersData.lectures.lectures.map((lecture) => [
        lecture.name_en || lecture.name_ar || "N/A",
        lecture.sales_count,
        `${lecture.total_revenue.toFixed(2)} KWD`,
        `${lecture.average_price.toFixed(2)} KWD`,
      ]);
    } else if (selectedTab === 2 && bestSellersData.notes?.notes) {
      return bestSellersData.notes.notes.map((note) => [
        note.name_en || note.name_ar || "N/A",
        note.sales_count,
        `${note.total_revenue.toFixed(2)} KWD`,
        `${note.average_price.toFixed(2)} KWD`,
      ]);
    }
    return [];
  };

  const bestSellersChartData = getBestSellersChartData();
  const bestSellersTableRows = getBestSellersTableRows();

  // Prepare subject performance data
  const subjectChartData = subjectData?.subjects?.map((subject) => ({
    name: subject.subject_name_en || subject.subject_name_ar || "N/A",
    sales: subject.sales_count,
    revenue: subject.total_revenue,
  })) || [];

  const subjectRows = subjectData?.subjects?.map((subject) => [
    subject.subject_name_en || subject.subject_name_ar || "N/A",
    subject.sales_count,
    `${subject.total_revenue.toFixed(2)} KWD`,
  ]) || [];

  // Prepare university performance data
  const universityChartData = universityData?.universities?.map((university) => ({
    name: university.university_name_en || university.university_name_ar || "N/A",
    sales: university.sales_count,
    revenue: university.total_revenue,
  })) || [];

  const universityRows = universityData?.universities?.map((university) => [
    university.university_name_en || university.university_name_ar || "N/A",
    university.sales_count,
    `${university.total_revenue.toFixed(2)} KWD`,
  ]) || [];

  // Prepare engagement data
  const courseEngagementRows = engagementData?.courses?.map((course) => [
    course.name_en || course.name_ar || "N/A",
    course.enrolled_users,
    course.users_with_progress,
    `${course.completion_rate.toFixed(2)}%`,
    `${course.average_progress.toFixed(2)}%`,
  ]) || [];

  const lectureEngagementRows = engagementData?.lectures?.map((lecture) => [
    lecture.name_en || lecture.name_ar || "N/A",
    lecture.enrolled_users,
    lecture.users_with_progress,
    `${lecture.completion_rate.toFixed(2)}%`,
    `${lecture.average_progress.toFixed(2)}%`,
  ]) || [];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        >
          <p style={{ margin: 0, fontWeight: "bold" }}>{payload[0].payload.name}</p>
          {payload.map((item, index) => (
            <p key={index} style={{ margin: "5px 0 0 0", color: item.color }}>
              {item.name}: {typeof item.value === "number" ? item.value.toFixed(2) : item.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const tabs = [
    {
      id: "courses",
      content: "Courses",
      panelID: "courses-panel",
    },
    {
      id: "lectures",
      content: "Lectures",
      panelID: "lectures-panel",
    },
    {
      id: "notes",
      content: "Notes",
      panelID: "notes-panel",
    },
  ];

  return (
    <Page
      title="Product Analytics"
      subtitle="Best sellers, performance by category, and engagement metrics"
      backAction={{ content: "Back", url: "/admin/analytics" }}
    >
      <BlockStack gap="500">
        <FilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          isLoading={isLoading}
        />

        {/* Best Sellers Section */}
        <Card>
          <Box padding="400">
            <BlockStack gap="400">
              <Text variant="headingLg" as="h2">
                Best Selling Products
              </Text>
              <Divider />
              <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
                <Box padding="400">
                  {bestSellersChartData.length > 0 ? (
                    <>
                      <div style={{ width: "100%", height: "400px", marginBottom: "20px" }}>
                        <ResponsiveContainer>
                          <BarChart data={bestSellersChartData} margin={{ bottom: 80 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="name"
                              style={{ fontSize: "10px" }}
                              angle={-45}
                              textAnchor="end"
                              height={80}
                            />
                            <YAxis style={{ fontSize: "12px" }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar dataKey="sales" fill={COLORS[0]} name="Sales Count" />
                            <Bar dataKey="revenue" fill={COLORS[1]} name="Revenue (KWD)" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <DataTable
                        columnContentTypes={["text", "numeric", "numeric", "numeric"]}
                        headings={["Name", "Sales Count", "Total Revenue", "Avg Price"]}
                        rows={bestSellersTableRows}
                      />
                    </>
                  ) : (
                    <Text>No data available for this product type</Text>
                  )}
                </Box>
              </Tabs>
            </BlockStack>
          </Box>
        </Card>

        <Layout>
          {/* Performance by Subject */}
          <Layout.Section variant="oneHalf">
            <Card>
              <Box padding="400">
                <BlockStack gap="400">
                  <Text variant="headingLg" as="h2">
                    Performance by Subject
                  </Text>
                  <Divider />
                  {subjectChartData.length > 0 ? (
                    <>
                      <div style={{ width: "100%", height: "400px" }}>
                        <ResponsiveContainer>
                          <BarChart data={subjectChartData} margin={{ bottom: 80 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="name"
                              style={{ fontSize: "10px" }}
                              angle={-45}
                              textAnchor="end"
                              height={80}
                            />
                            <YAxis style={{ fontSize: "12px" }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar dataKey="revenue" fill={COLORS[2]} name="Revenue (KWD)" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <DataTable
                        columnContentTypes={["text", "numeric", "numeric"]}
                        headings={["Subject", "Sales Count", "Total Revenue"]}
                        rows={subjectRows}
                      />
                    </>
                  ) : (
                    <Text>No subject data available</Text>
                  )}
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>

          {/* Performance by University */}
          <Layout.Section variant="oneHalf">
            <Card>
              <Box padding="400">
                <BlockStack gap="400">
                  <Text variant="headingLg" as="h2">
                    Performance by University
                  </Text>
                  <Divider />
                  {universityChartData.length > 0 ? (
                    <>
                      <div style={{ width: "100%", height: "400px" }}>
                        <ResponsiveContainer>
                          <BarChart data={universityChartData} margin={{ bottom: 80 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="name"
                              style={{ fontSize: "10px" }}
                              angle={-45}
                              textAnchor="end"
                              height={80}
                            />
                            <YAxis style={{ fontSize: "12px" }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar dataKey="revenue" fill={COLORS[3]} name="Revenue (KWD)" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <DataTable
                        columnContentTypes={["text", "numeric", "numeric"]}
                        headings={["University", "Sales Count", "Total Revenue"]}
                        rows={universityRows}
                      />
                    </>
                  ) : (
                    <Text>No university data available</Text>
                  )}
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>

        {/* Engagement Metrics */}
        <Card>
          <Box padding="400">
            <BlockStack gap="500">
              <Text variant="headingLg" as="h2">
                Engagement Metrics
              </Text>
              <Divider />

              {/* Course Engagement */}
              <Box>
                <Text variant="headingMd" as="h3" fontWeight="semibold">
                  Course Completion Rates
                </Text>
                {courseEngagementRows.length > 0 ? (
                  <>
                    <div style={{ marginTop: "16px" }}>
                      {engagementData.courses.slice(0, 10).map((course, index) => {
                        const progress = course.completion_rate;
                        return (
                          <div key={index} style={{ marginTop: "12px" }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: "4px",
                              }}
                            >
                              <Text variant="bodySm" as="span">
                                {course.name_en || course.name_ar || "N/A"}
                              </Text>
                              <Text variant="bodySm" as="span" fontWeight="semibold">
                                {course.completion_rate.toFixed(2)}% ({course.users_with_progress}/
                                {course.enrolled_users} users)
                              </Text>
                            </div>
                            <ProgressBar progress={progress} size="small" />
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: "20px" }}>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ borderBottom: "1px solid #e1e3e5" }}>
                              <th style={{ padding: "12px", textAlign: "left", fontWeight: 600 }}>Course</th>
                              <th style={{ padding: "12px", textAlign: "right", fontWeight: 600 }}>Enrolled Users</th>
                              <th style={{ padding: "12px", textAlign: "right", fontWeight: 600 }}>Users with Progress</th>
                              <th style={{ padding: "12px", textAlign: "right", fontWeight: 600 }}>Completion Rate</th>
                              <th style={{ padding: "12px", textAlign: "right", fontWeight: 600 }}>Avg Progress</th>
                            </tr>
                          </thead>
                          <tbody>
                            {engagementData.courses.map((course, index) => (
                              <tr key={index} style={{ borderBottom: "1px solid #e1e3e5" }}>
                                <td style={{ padding: "12px" }}>{course.name_en || course.name_ar || "N/A"}</td>
                                <td style={{ padding: "12px", textAlign: "right" }}>
                                  <Button
                                    plain
                                    onClick={() => handleViewEnrolledUsers('course', course.course_id, course.name_en || course.name_ar)}
                                  >
                                    {course.enrolled_users}
                                  </Button>
                                </td>
                                <td style={{ padding: "12px", textAlign: "right" }}>{course.users_with_progress}</td>
                                <td style={{ padding: "12px", textAlign: "right" }}>{course.completion_rate.toFixed(2)}%</td>
                                <td style={{ padding: "12px", textAlign: "right" }}>{course.average_progress.toFixed(2)}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <Text>No course engagement data available</Text>
                )}
              </Box>

              <Divider />

              {/* Lecture Engagement */}
              <Box>
                <Text variant="headingMd" as="h3" fontWeight="semibold">
                  Lecture Completion Rates
                </Text>
                {lectureEngagementRows.length > 0 ? (
                  <>
                    <div style={{ marginTop: "16px" }}>
                      {engagementData.lectures.slice(0, 10).map((lecture, index) => {
                        const progress = lecture.completion_rate;
                        return (
                          <div key={index} style={{ marginTop: "12px" }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: "4px",
                              }}
                            >
                              <Text variant="bodySm" as="span">
                                {lecture.name_en || lecture.name_ar || "N/A"}
                              </Text>
                              <Text variant="bodySm" as="span" fontWeight="semibold">
                                {lecture.completion_rate.toFixed(2)}% ({lecture.users_with_progress}/
                                {lecture.enrolled_users} users)
                              </Text>
                            </div>
                            <ProgressBar progress={progress} size="small" />
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: "20px" }}>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ borderBottom: "1px solid #e1e3e5" }}>
                              <th style={{ padding: "12px", textAlign: "left", fontWeight: 600 }}>Lecture</th>
                              <th style={{ padding: "12px", textAlign: "right", fontWeight: 600 }}>Enrolled Users</th>
                              <th style={{ padding: "12px", textAlign: "right", fontWeight: 600 }}>Users with Progress</th>
                              <th style={{ padding: "12px", textAlign: "right", fontWeight: 600 }}>Completion Rate</th>
                              <th style={{ padding: "12px", textAlign: "right", fontWeight: 600 }}>Avg Progress</th>
                            </tr>
                          </thead>
                          <tbody>
                            {engagementData.lectures.map((lecture, index) => (
                              <tr key={index} style={{ borderBottom: "1px solid #e1e3e5" }}>
                                <td style={{ padding: "12px" }}>{lecture.name_en || lecture.name_ar || "N/A"}</td>
                                <td style={{ padding: "12px", textAlign: "right" }}>
                                  <Button
                                    plain
                                    onClick={() => handleViewEnrolledUsers('lecture', lecture.lecture_id, lecture.name_en || lecture.name_ar)}
                                  >
                                    {lecture.enrolled_users}
                                  </Button>
                                </td>
                                <td style={{ padding: "12px", textAlign: "right" }}>{lecture.users_with_progress}</td>
                                <td style={{ padding: "12px", textAlign: "right" }}>{lecture.completion_rate.toFixed(2)}%</td>
                                <td style={{ padding: "12px", textAlign: "right" }}>{lecture.average_progress.toFixed(2)}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <Text>No lecture engagement data available</Text>
                )}
              </Box>
            </BlockStack>
          </Box>
        </Card>
      </BlockStack>

      {/* Enrolled Users Modal */}
      <Modal
        open={modalActive}
        onClose={handleCloseModal}
        title={`Enrolled Users - ${modalData?.productName || ''}`}
        large
      >
        <Modal.Section>
          {modalLoading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spinner size="large" />
              <div style={{ marginTop: "16px" }}>
                <Text>Loading enrolled users...</Text>
              </div>
            </div>
          ) : modalData ? (
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h3">
                  Total: {modalData.total} enrolled users
                </Text>
              </InlineStack>
              <Divider />
              {modalData.enrolled_users && modalData.enrolled_users.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #e1e3e5", backgroundColor: "#f6f6f7" }}>
                        <th style={{ padding: "12px", textAlign: "left", fontWeight: 600 }}>Name</th>
                        <th style={{ padding: "12px", textAlign: "left", fontWeight: 600 }}>Phone</th>
                        {modalType === 'course' && (
                          <th style={{ padding: "12px", textAlign: "right", fontWeight: 600 }}>Progress</th>
                        )}
                        <th style={{ padding: "12px", textAlign: "center", fontWeight: 600 }}>Status</th>
                        <th style={{ padding: "12px", textAlign: "right", fontWeight: 600 }}>Price Paid</th>
                        <th style={{ padding: "12px", textAlign: "left", fontWeight: 600 }}>Purchase Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalData.enrolled_users.map((user, index) => (
                        <tr
                          key={index}
                          style={{
                            borderBottom: "1px solid #e1e3e5",
                            backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafbfb"
                          }}
                        >
                          <td style={{ padding: "12px" }}>{user.name}</td>
                          <td style={{ padding: "12px" }}>{user.phone || 'N/A'}</td>
                          {modalType === 'course' && (
                            <td style={{ padding: "12px", textAlign: "right" }}>{user.progress}%</td>
                          )}
                          <td style={{ padding: "12px", textAlign: "center" }}>
                            <Badge tone={user.is_complete ? "success" : "info"}>
                              {user.is_complete ? "Completed" : "In Progress"}
                            </Badge>
                          </td>
                          <td style={{ padding: "12px", textAlign: "right" }}>{user.purchased_price} KWD</td>
                          <td style={{ padding: "12px" }}>
                            {new Date(user.purchased_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Text>No enrolled users found.</Text>
              )}
            </BlockStack>
          ) : (
            <Text>Error loading data</Text>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default Products;
